/*
 *  Copyright 2026 LiteFarm.org
 *  This file is part of LiteFarm.
 *
 *  LiteFarm is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  LiteFarm is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 *  GNU General Public License for more details, see <https://www.gnu.org/licenses/>.
 */

/**
 * scanPassportSlice — Phase 8 / Plan 08-01 scaffold.
 *
 * Owns the per-page review queue + status state machine for the
 * "Scan passport" modal launched from the Animals Inventory page.
 *
 * State machine (LD-14 + LD-22 phased-spinner contract):
 *   'idle' -> 'uploading' -> 'processing' -> 'reviewing' -> 'saving' -> 'done' | 'error'
 *
 * DO NOT collapse the 'processing' status into 'uploading' — Plan 08-02's UX
 * depends on this enum being four-phased (LD-22 phased spinner).
 *
 * Action surface (ALL must remain — Plans 08-02..08-05 wire to these):
 *   - openModal / closeModal
 *   - uploadStarted / uploadProgress / processingStarted
 *   - pagesReceived (success path)
 *   - pageSkipped / pageUnskipped
 *   - pageUpdateExisting / pageSaveAsNew (LD-17 duplicate handling)
 *   - pageFormValuesChanged (form mirror — Plan 08-03 wires)
 *   - savingStarted / saveSucceeded / saveFailed
 *   - scanFailed (transport-level failure, LD-22)
 *
 * Entity = one review page; selectId = pageIndex (0-based, per LD-14).
 * Per-page UI state appended at pagesReceived time:
 *   - skipped: boolean
 *   - mode: 'new' | 'update' | 'new_with_override'
 *   - target_animal_id: number | null
 *   - formValues: object (mirror of mapped fields for ReviewAnimalDetails to bind via FormProvider)
 *
 * State path: state.entitiesReducer.scanPassportReducer.* (matches orthophoto precedent).
 *
 * LD-21 Retake-whole-file: originalFiles is cached on uploadStarted so Plan 08-04
 * can re-fire the upload without re-prompting the file picker. closeModal /
 * saveSucceeded reset to initialState which clears the cache (T-08-01-04 mitigation).
 */

import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

const reviewPagesAdapter = createEntityAdapter({
  selectId: (page) => page.pageIndex,
});

const initialState = reviewPagesAdapter.getInitialState({
  status: 'idle', // 'idle' | 'uploading' | 'processing' | 'reviewing' | 'saving' | 'done' | 'error'
  uploadProgress: 0,
  error: null, // { code, message } | null
  isOpen: false,
  originalFiles: [], // LD-21 Retake cache — File[] from uploadStarted payload
  // Global "apply to all" fields — user fills once in ReviewQueue's bulk panel
  // and saveAllSaga injects them into every page's payload. Required-manual per
  // user direction (verify-work Test 7): origin_id (vrsta dolaska) +
  // brought_in_date (datum dolaska) are NOT extracted from the passport.
  bulkFields: { origin_id: '', brought_in_date: '', location_id: '' },
});

const scanPassportSlice = createSlice({
  name: 'scanPassportReducer',
  initialState,
  reducers: {
    openModal(state) {
      state.isOpen = true;
      state.status = 'idle';
      state.error = null;
    },
    // Full reset on close — clears originalFiles cache (T-08-01-04)
    closeModal: () => initialState,

    uploadStarted(state, { payload }) {
      // payload = { files: File[] }
      state.status = 'uploading';
      state.uploadProgress = 0;
      state.error = null;
      state.originalFiles = payload?.files ?? [];
    },
    uploadProgress(state, { payload }) {
      // payload = number 0..100
      state.uploadProgress = payload;
    },
    processingStarted(state) {
      state.status = 'processing';
    },
    pagesReceived(state, { payload }) {
      // payload = Array<MappedAnimalDraft> per Phase 7 LD-5
      // Auto-skip pages whose zivotni_broj already exists on this farm — the
      // backend tags them with `duplicate: { animal_id, zivotni_broj }`. User
      // can still un-skip from the review queue if they want to update the
      // existing record.
      const entities = (payload || []).map((page, i) => ({
        pageIndex: i,
        ...page,
        // UI state per page (LD-14):
        skipped: Boolean(page?.duplicate),
        mode: 'new', // 'new' | 'update' | 'new_with_override'
        target_animal_id: null,
        formValues: { ...page }, // mirror of mapped fields for ReviewAnimalDetails
      }));
      reviewPagesAdapter.setAll(state, entities);
      state.status = 'reviewing';
    },
    pageSkipped(state, { payload }) {
      // payload = pageIndex
      reviewPagesAdapter.updateOne(state, { id: payload, changes: { skipped: true } });
    },
    pageUnskipped(state, { payload }) {
      // payload = pageIndex
      reviewPagesAdapter.updateOne(state, { id: payload, changes: { skipped: false } });
    },
    pageUpdateExisting(state, { payload }) {
      // payload = { pageIndex, target_animal_id }
      const { pageIndex, target_animal_id } = payload;
      reviewPagesAdapter.updateOne(state, {
        id: pageIndex,
        changes: { mode: 'update', target_animal_id },
      });
    },
    pageSaveAsNew(state, { payload }) {
      // payload = pageIndex — clears zivotni_broj to force user re-entry (LD-17 / P-6)
      const pageIndex = payload;
      const existing = state.entities[pageIndex];
      const existingFormValues = (existing && existing.formValues) || {};
      reviewPagesAdapter.updateOne(state, {
        id: pageIndex,
        changes: {
          mode: 'new_with_override',
          formValues: { ...existingFormValues, zivotni_broj: null },
        },
      });
    },
    pageFormValuesChanged(state, { payload }) {
      // payload = { pageIndex, formValues }
      const { pageIndex, formValues } = payload;
      reviewPagesAdapter.updateOne(state, { id: pageIndex, changes: { formValues } });
    },
    // Global "apply to all" fields — vrsta dolaska + datum dolaska are
    // required-manual; user fills once and saveAllSaga merges into every page.
    bulkFieldsChanged(state, { payload }) {
      // payload = { origin_id?, brought_in_date? } — partial OR full update
      state.bulkFields = { ...state.bulkFields, ...(payload || {}) };
    },
    // MED-1 partial-success retry dedup (Plan 08-05): saveAllSaga flips each
    // successfully-POSTed/PATCHed page to `saved: true`. selectActivePages
    // excludes saved pages so a partial-failure retry does NOT re-send already
    // committed animals (which would 23505-conflict on the unique partial index
    // on (farm_id, zivotni_broj) from Phase 6 DB-02).
    pagesSaved(state, { payload }) {
      // payload = { pageIndices: number[] }
      const indices = payload?.pageIndices ?? [];
      for (const id of indices) {
        if (state.entities[id]) {
          reviewPagesAdapter.updateOne(state, { id, changes: { saved: true } });
        }
      }
    },
    savingStarted(state) {
      state.status = 'saving';
      state.error = null;
    },
    // Full reset on success — closes modal + clears originalFiles cache
    saveSucceeded: () => initialState,
    saveFailed(state, { payload }) {
      // payload = { code, message }
      // LD-16-amended: queue stays intact; user can fix + retry
      state.status = 'reviewing';
      state.error = payload || null;
    },
    scanFailed(state, { payload }) {
      // payload = { code, message } — transport-level scan failure (e.g. 503 OCR_SERVICE_UNAVAILABLE)
      state.status = 'error';
      state.error = payload || null;
    },
  },
});

// Action creators
export const {
  openModal,
  closeModal,
  uploadStarted,
  uploadProgress,
  processingStarted,
  pagesReceived,
  pageSkipped,
  pageUnskipped,
  pageUpdateExisting,
  pageSaveAsNew,
  pageFormValuesChanged,
  bulkFieldsChanged,
  pagesSaved,
  savingStarted,
  saveSucceeded,
  saveFailed,
  scanFailed,
} = scanPassportSlice.actions;

// Reducer (default export — registered in store/reducer.js)
export default scanPassportSlice.reducer;

// === Selectors (orthophoto precedent — state.entitiesReducer[slice.name]) ===

export const scanPassportStateSelector = (state) =>
  state.entitiesReducer[scanPassportSlice.name];

const pagesSelectors = reviewPagesAdapter.getSelectors(
  (state) => state.entitiesReducer[scanPassportSlice.name],
);

export const selectAllPages = pagesSelectors.selectAll;
export const selectPageEntities = pagesSelectors.selectEntities;
export const selectPageById = pagesSelectors.selectById;
export const selectPageIds = pagesSelectors.selectIds;
export const selectPageCount = pagesSelectors.selectTotal;

export const selectStatus = createSelector(
  [scanPassportStateSelector],
  (s) => s?.status ?? 'idle',
);

export const selectError = createSelector(
  [scanPassportStateSelector],
  (s) => s?.error ?? null,
);

export const selectIsOpen = createSelector(
  [scanPassportStateSelector],
  (s) => Boolean(s?.isOpen),
);

export const selectUploadProgress = createSelector(
  [scanPassportStateSelector],
  (s) => s?.uploadProgress ?? 0,
);

export const selectOriginalFiles = createSelector(
  [scanPassportStateSelector],
  (s) => s?.originalFiles ?? [],
);

export const selectBulkFields = createSelector(
  [scanPassportStateSelector],
  (s) =>
    s?.bulkFields ?? {
      origin_id: '',
      brought_in_date: '',
      location_id: '',
    },
);

// Save-all gate: true when any required global bulk field is empty. Surfaced
// inline in the bulk panel + read by saveAllSaga to short-circuit before
// POST/PATCH. location_id is intentionally NOT gated — the user can assign a
// location later via the dedicated 'move animals' task, so we let the bulk
// save proceed without it.
export const selectSaveBlockedByBulkFields = createSelector(
  [selectBulkFields],
  (b) => !b?.origin_id || !b?.brought_in_date,
);

// Active = not skipped (LD-17 — Skip excludes from save-all) AND not saved
// (MED-1 — already-committed pages from a partial-success retry are excluded
// so a re-fire of saveAll does NOT re-send them).
export const selectActivePages = createSelector(
  [selectAllPages],
  (pages) => pages.filter((p) => !p.skipped && p.saved !== true),
);

// Pages that have been successfully POSTed/PATCHed in a prior save-all attempt.
// Used by the success snackbar count + MED-1 retry-dedup invariant assertions.
export const selectSavedPages = createSelector(
  [selectAllPages],
  (pages) => pages.filter((p) => p.saved === true),
);

// P-6 safeguard (Plan 08-05 programmatic enforcement): true if any active page
// is in 'new_with_override' mode AND its formValues.zivotni_broj is null/empty.
// Plan 08-04 surfaces the inline red Alert; Plan 08-05 refuses to dispatch
// saveAll while this returns true.
export const selectSaveBlockedByP6 = createSelector(
  [selectActivePages],
  (pages) =>
    pages.some(
      (p) =>
        p.mode === 'new_with_override' &&
        (!p.formValues || p.formValues.zivotni_broj == null || p.formValues.zivotni_broj === ''),
    ),
);

export const selectBlockedPageIndices = createSelector(
  [selectActivePages],
  (pages) =>
    pages
      .filter(
        (p) =>
          p.mode === 'new_with_override' &&
          (!p.formValues || p.formValues.zivotni_broj == null || p.formValues.zivotni_broj === ''),
      )
      .map((p) => p.pageIndex),
);

// Factory selector: returns active pages filtered by `mode`.
// Memoized via createSelector — caller can re-call with same mode to hit cache.
// NOTE: if multiple callers pass different `mode` values, this returns the most-recent.
// Use a memoize-by-arg wrapper or selectActivePages + caller-side filter for stricter
// memoization. For Plan 08-05's usage (saveAllSaga reads 'new'/'new_with_override' once
// then 'update' once) this is sufficient.
export const selectPagesByMode = (mode) =>
  createSelector([selectActivePages], (pages) => pages.filter((p) => p.mode === mode));

// Pages that the reviewer must visit before save-all:
// - have unrecoverable fields flagged by OCR/mapper (ERR-03)
// - are duplicates (LD-17)
// - are mapping-degraded (LD-11 _mapping_error)
export const selectPagesNeedingReview = createSelector(
  [selectAllPages],
  (pages) =>
    pages.filter(
      (p) =>
        (Array.isArray(p._unrecoverable_fields) && p._unrecoverable_fields.length > 0) ||
        p.duplicate ||
        p._mapping_error,
    ),
);
